"use client";

/**
 * Notifications provider use react-toastify and toast, watching store for notifications in the app
 *
 * @see https://fkhadra.github.io/react-toastify/introduction
 */

import { createContext, useCallback, useContext, useMemo } from "react";
import { Bounce, ToastContainer, toast } from "react-toastify";

type NotificationType = "success" | "info" | "warning" | "error";

type NotificationsContextType = {
  notify: (type: string, message: string) => void;
};
type NotificationsProviderProps = {
  children: React.ReactNode;
};

const NotificationsContext = createContext<
  NotificationsContextType | undefined
>(undefined);

const NotificationsProvider = ({ children }: NotificationsProviderProps) => {
  const notify = useCallback((type: string, message: string) => {
    const validTypes: NotificationType[] = [
      "success",
      "info",
      "warning",
      "error"
    ];
    if (validTypes.includes(type as NotificationType)) {
      toast[type as NotificationType](message);
    } else {
      toast(message);
    }
  }, []);

  const contextValue = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationsContext.Provider value={contextValue}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
        className="p-4 z-50"
        toastClassName="rounded-md shadow-lg"
      />
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationsProvider"
    );
  }
  return context;
};

export default NotificationsProvider;
